import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { User, Store, ArrowRight } from "lucide-react";
import JumlatyLogo from "../components/JumlatyLogo";

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  // accountType: null (chooser) | "regular" (no iPad/market) | "market" (full form)
  const [accountType, setAccountType] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [marketName, setMarketName] = useState("");
  const [location, setLocation] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [coords] = useState(null); // location moved to Profile — kept as placeholder for payload compatibility
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Location capture moved to MyAccount (Profile) page — see MyAccount.jsx.

  const isMarket = accountType === "market";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("كلمة السر غير متطابقة");
      return;
    }

    if (password.length < 6) {
      setError("كلمة السر يجب أن تكون 6 أحرف على الأقل");
      return;
    }

    if (!customerName.trim()) {
      setError("الاسم مطلوب");
      return;
    }

    // Phone must be exactly 11 digits (Iraqi format 07XXXXXXXXX).
    // Requested Feb 27, 2026 — block 10 / 12 / 13-digit typos at the source.
    if (!/^\d{11}$/.test(phone.trim())) {
      setError("تأكد من رقم الهاتف — يجب أن يكون 11 رقم بالضبط");
      return;
    }

    // GPS is OPTIONAL at signup (Feb 27, 2026) — customer can pin it
    // later from Profile → موقعي على الخريطة. The driver still gets a
    // text address, and the pin can be completed before the first order.

    // Validate customer_id only for market accounts (regular customers don't have iPad)
    if (isMarket && customerId.trim()) {
      const idStr = customerId.trim();
      if (!/^\d+$/.test(idStr)) {
        setError("رقم الايباد يجب أن يكون أرقاماً فقط");
        return;
      }
      const idNum = parseInt(idStr, 10);
      if (idNum < 1 || idNum > 999) {
        setError("رقم الايباد يجب أن يكون من 1 إلى 999 فقط");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        phone,
        customer_name: customerName,
        location,
        password,
        is_market_owner: isMarket,
      };
      if (isMarket) {
        payload.customer_id = customerId;
        payload.market_name = marketName;
      }
      if (coords) {
        payload.lat = coords.lat;
        payload.lng = coords.lng;
      }
      const user = await signup(payload);
      toast.success(`مرحباً ${user.full_name}! تم إنشاء الحساب بنجاح`);
      navigate("/");
    } catch (err) {
      const msg = err.response?.data?.detail || "حدث خطأ، حاول مرة أخرى";
      setError(typeof msg === "string" ? msg : "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  // ─── STEP 1: account-type chooser ───
  if (accountType === null) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <JumlatyLogo size="xl" showTagline />
            </Link>
            <p className="text-[#1E3A5F] font-bold text-lg mt-3">اختر نوع الحساب</p>
            <p className="text-[#5C6E64] text-sm mt-1">حدّد كيف ستستخدم جُملتي</p>
          </div>

          <div className="grid grid-cols-1 gap-4 bg-white/85 backdrop-blur-md border border-[#BAE6FD] rounded-3xl p-6 sm:p-8 shadow-xl">
            {/* Regular Customer */}
            <button
              type="button"
              onClick={() => setAccountType("regular")}
              data-testid="signup-type-regular"
              className="relative bg-gradient-to-l from-[#3B82F6] to-[#1E3A5F] text-white rounded-2xl p-5 text-right shadow-md hover:shadow-xl transition-all active:scale-95 group"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="w-5 h-5" />
                    <h3 className="font-black text-lg">زبون عادي</h3>
                  </div>
                  <p className="text-xs opacity-90">
                    للأفراد — اسم + عنوان + هاتف فقط، بدون رقم ايباد أو ماركت.
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 opacity-70 group-hover:translate-x-[-4px] transition-transform" />
              </div>
            </button>

            {/* Market Owner */}
            <button
              type="button"
              onClick={() => setAccountType("market")}
              data-testid="signup-type-market"
              className="relative bg-gradient-to-l from-[#7ED321] to-[#15803d] text-white rounded-2xl p-5 text-right shadow-md hover:shadow-xl transition-all active:scale-95 group"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Store className="w-5 h-5" />
                    <h3 className="font-black text-lg">لدي ماركت</h3>
                  </div>
                  <p className="text-xs opacity-90">
                    لأصحاب المحلات والماركتات — اسم ماركت + رقم ايباد + موقع GPS للتوصيل.
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 opacity-70 group-hover:translate-x-[-4px] transition-transform" />
              </div>
            </button>

            <p className="text-center text-sm text-[#5C6E64] pt-2 border-t border-[#E4EBE6]">
              لديك حساب بالفعل؟{" "}
              <Link to="/login" data-testid="goto-login" className="text-[#2D4A3E] font-medium hover:underline">
                تسجيل الدخول
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // ─── STEP 2: form (varies by account type) ───
  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <JumlatyLogo size="xl" showTagline />
          </Link>
          <p className="text-[#1E3A5F] font-medium mt-3">
            إنشاء حساب — {isMarket ? "صاحب ماركت" : "زبون عادي"}
          </p>
          <button
            type="button"
            onClick={() => setAccountType(null)}
            data-testid="signup-change-type"
            className="text-xs text-[#3B82F6] hover:underline mt-1 inline-flex items-center gap-1"
          >
            تغيير نوع الحساب
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 bg-white/85 backdrop-blur-md border border-[#BAE6FD] rounded-3xl p-6 sm:p-8 shadow-xl"
        >
          {/* Name — common */}
          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">
              {isMarket ? "اسم الزبون *" : "الاسم الكامل *"}
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              data-testid="signup-customer-name"
              className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
              placeholder={isMarket ? "اسم الزبون" : "مثال: علي حسن"}
              required
            />
          </div>

          {/* iPad number + market name — ONLY for market accounts */}
          {isMarket && (
            <>
              <div>
                <label className="block text-sm font-medium text-[#111814] mb-2">
                  رقم الايباد
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={3}
                  value={customerId}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d]/g, "").slice(0, 3);
                    setCustomerId(v);
                  }}
                  data-testid="signup-customer-id"
                  className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                  placeholder="من 1 إلى 999"
                />
                <p className="text-xs text-[#5C6E64] mt-1">رقم خاص بك من 1 إلى 999 للبحث السريع في الإدارة (اختياري)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111814] mb-2">
                  لدي ماركت
                </label>
                <input
                  type="text"
                  value={marketName}
                  onChange={(e) => setMarketName(e.target.value)}
                  data-testid="signup-market-name"
                  className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                  placeholder="مثلاً: ماركت النور"
                />
              </div>
            </>
          )}

          {/* Address — common */}
          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">
              {isMarket ? "عنوان الماركت" : "العنوان"}
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              data-testid="signup-location"
              className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
              placeholder="المدينة، الحي، الشارع..."
            />
          </div>

          {/* GPS — moved to Profile page (Feb 27, 2026, requested by owner).
              Customers now sign up without pinning; they can add or edit
              their map pin anytime from إدارة حسابي → موقعي على الخريطة. */}

          {/* Phone — common (validated: exactly 11 digits) */}
          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">
              رقم الموبايل *
            </label>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={11}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
              data-testid="signup-phone"
              className={`w-full px-4 py-3 rounded-2xl border focus:outline-none focus:ring-2 bg-white ${
                phone.length > 0 && phone.length !== 11
                  ? "border-[#E85D45] ring-1 ring-[#E85D45]/40 focus:ring-[#E85D45]"
                  : "border-[#E4EBE6] focus:ring-[#2D4A3E]"
              }`}
              placeholder="07XXXXXXXXX"
              required
            />
            {phone.length > 0 && phone.length !== 11 && (
              <p
                data-testid="signup-phone-hint"
                className="mt-1.5 text-[12px] font-bold text-[#E85D45] flex items-center gap-1"
              >
                ⚠️ تأكد من رقم الهاتف — يجب أن يكون 11 رقم بالضبط ({phone.length}/11)
              </p>
            )}
          </div>

          {/* Password — common */}
          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">
              كلمة السر *
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="signup-password"
              className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
              placeholder="6 أحرف على الأقل"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">
              تأكيد كلمة السر *
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="signup-confirm-password"
              className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
              placeholder="أعد كتابة كلمة السر"
              required
            />
          </div>

          {error && (
            <p data-testid="signup-error" className="text-[#E85D45] text-sm">
              {error}
            </p>
          )}

          <motion.button
            type="submit"
            disabled={loading}
            whileTap={{ scale: 0.98 }}
            data-testid="signup-submit"
            className="w-full bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-3 rounded-full transition-colors disabled:opacity-50"
          >
            {loading ? "جاري إنشاء الحساب..." : "إنشاء حساب"}
          </motion.button>

          <p className="text-center text-sm text-[#5C6E64]">
            لديك حساب بالفعل؟{" "}
            <Link
              to="/login"
              data-testid="goto-login"
              className="text-[#2D4A3E] font-medium hover:underline"
            >
              تسجيل الدخول
            </Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default Signup;
